import { useCallback, useEffect, useMemo, useState } from 'react';

import type { IServerListQueryParams } from '@/shared/types/serverPagination';

export type ServerTableSortDirection = 'asc' | 'desc';

export interface IUseServerListTableStateOptions {
  pageSize?: number;
  /**
   * Maps DataTable column `key` values to API `sort_by` field names.
   * Example: `{ username: 'first_name', created_at: 'created_at' }` → asc `first_name`, desc `-first_name`.
   */
  sortFieldByColumnKey?: Partial<Record<string, string>>;
  initialSort?: { columnKey: string; direction: ServerTableSortDirection };
}

export interface IServerListTableState {
  page: number;
  setPage: (page: number) => void;
  pageSize: number;
  /** API params: limit, offset, sort_by, search, etc. */
  listParams: IServerListQueryParams;
  resetPage: () => void;
  /** Current sort column (DataTable `ITableColumn.key`). Undefined until user sorts (unless initialSort). */
  sortColumnKey: string | undefined;
  sortDirection: ServerTableSortDirection;
  onSortChange: (columnKey: string, direction: ServerTableSortDirection) => void;
  search: string;
  setSearch: (query: string) => void;
}

/**
 * Shared pagination + sort_by + search for server-driven lists.
 * `listParams.search` follows `search` immediately. Debounce server search in `TanStackTable` (or locally for custom inputs).
 */
export function useServerListTableState(
  options: IUseServerListTableStateOptions = {}
): IServerListTableState {
  const pageSize = options.pageSize ?? 10;

  const [page, setPage] = useState(1);
  const [sortColumnKey, setSortColumnKey] = useState<string | undefined>(
    options.initialSort?.columnKey
  );
  const [sortDirection, setSortDirection] = useState<ServerTableSortDirection>(
    options.initialSort?.direction ?? 'asc'
  );
  const [search, setSearchState] = useState('');

  useEffect(() => {
    setPage(1);
  }, [search]);

  const resetPage = useCallback(() => setPage(1), []);

  const setSearch = useCallback((query: string) => {
    setSearchState(query);
  }, []);

  const onSortChange = useCallback((columnKey: string, direction: ServerTableSortDirection) => {
    setSortColumnKey(columnKey);
    setSortDirection(direction);
    setPage(1);
  }, []);

  const listParams = useMemo((): IServerListQueryParams => {
    const map = options.sortFieldByColumnKey ?? {};
    const field = sortColumnKey ? (map[sortColumnKey] ?? sortColumnKey) : undefined;
    let sort_by: string | undefined;
    if (field && sortColumnKey) {
      sort_by = sortDirection === 'desc' ? `-${field}` : field;
    }
    const q = search.trim();
    return {
      limit: pageSize,
      offset: (page - 1) * pageSize,
      ...(sort_by ? { sort_by } : {}),
      ...(q ? { search: q } : {}),
    };
    // Omit `options.sortFieldByColumnKey` identity — inline maps from callers would recreate
    // listParams every render and infinite-loop dependent fetches.
  }, [page, pageSize, sortColumnKey, sortDirection, search]); // eslint-disable-line react-hooks/exhaustive-deps -- sort map is static per page

  return {
    page,
    setPage,
    pageSize,
    listParams,
    resetPage,
    sortColumnKey,
    sortDirection,
    onSortChange,
    search,
    setSearch,
  };
}
